/**
 * Formatting for AI usage figures (spec 2026-08-16-08 §3/§4).
 *
 * Shared between the import dashboard's cost stat and the jobs page's org-wide
 * card so the same number never renders two different ways.
 */

/**
 * Formats a USD cost. AI costs span five orders of magnitude — a single
 * embedding is fractions of a cent, a whole deposit is dollars — so a fixed
 * precision either rounds small numbers to $0.00 (reading as "free", which is
 * the wrong answer) or drowns large ones in decimals. Small amounts therefore
 * get more digits, and a genuinely non-zero cost never renders as $0.00.
 */
export function formatAICost(costUsd: number, locale: string): string {
    const abs = Math.abs(costUsd)
    let digits = 2
    // Exactly 0 keeps the plain 2 digits: "$0.00" is the honest rendering of
    // "nothing was spent", whereas "$0.0000" reads as a rounded-away amount.
    if (abs > 0 && abs < 0.01) digits = 4
    else if (abs > 0 && abs < 1) digits = 3

    const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(costUsd)

    return formatted
}

/**
 * Formats a token count compactly (48210 → "48k"). Token counts are only ever
 * read as an order of magnitude, and the exact figure is one API call away.
 */
export function formatTokens(tokens: number, locale: string): string {
    return new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: tokens < 10_000 ? 1 : 0,
    }).format(tokens)
}
